import _ from 'lodash'
import * as constants from '@adc/constants'
import configment from '@adc/configment'
import type {
  AdcFetch,
  FetchOptions,
  PatchedResponse,
  NodeFetchOptions,
  BrowserFetchOptions,
  MethodFetchInvocationOptions,
  HTTPMethod,
  StringIndexable,
  HookFetchOptions,
} from './types'
import wellKnownSymbols from './wellKnownSymbols'

// This lets us access the types of the FetchOptions properties.
declare var fetchOptionProps: FetchOptions

const HTTP_FETCH_FAILURE_CODE = 0
const ADC_FETCH_USER_AGENT = 'adc/fetch'

export const defaultStaticFetchOptions: HookFetchOptions = {
  fetchOptionsCheck: (_url, options) => options,
}

export const serializeParams = (params: Record<string, string>) =>
  Object.keys(params)
    .map(
      (key: string) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join('&')

export const constructUrl = (url: string, params = {}) => {
  if (_.isEmpty(params)) {
    return url
  }

  // If we have a string, this is skipped but we'll need to serialize if we've been given an object
  if (_.isPlainObject(params)) params = serializeParams(params)

  return `${url}?${params}`
}

export const userAgent = () =>
  [ADC_FETCH_USER_AGENT, configment.get('APP_NAME', '').replace(/-/g, '_')]
    .filter(Boolean)
    .join('/')

interface Indexable {
  [index: string]: any
}

// If we serialize an object to JSON, we can force the correct content. Otherwise, the caller
// should manually serialize the content.
export const attemptAutomaticBodySerialization = (options: FetchOptions) => {
  if (
    options.body &&
    typeof options.body === 'object' &&
    (options.headers as StringIndexable)['Content-Type'] === 'application/json'
  ) {
    options.body = JSON.stringify(options.body)
    ;(options.headers as Indexable)['Content-Type'] = 'application/json'
  }
}

// NOTE: Strip any options that you don't want passed through to the global fetch here.
export const stripNonStandardFetchOptions = (
  options: NodeFetchOptions & BrowserFetchOptions
) => {
  // delete options.queryParams
  // delete options.parseResponse
  delete options.useProxy
  delete options.req

  stripUnwantedHeaders(options.headers)
}

const stripUnwantedHeaders = (headers: typeof fetchOptionProps.headers) => {
  for (const [key, value] of Object.entries(headers as Indexable)) {
    if (value === wellKnownSymbols.removeHeader) {
      delete (headers as Indexable)[key]
    }
  }
}

export const executeRequest = (
  url: string,
  options: RequestInit,
  executeOptions: any
) => {
  const { timeoutID, fetchOptionsCheck } = executeOptions

  executeOptions.eventCapture.on('request:start', {
    uri: url,
    body: options.body,
    headers: options.headers,
    xcid: (options.headers as Indexable)['x-cid'] || null,
    userId: null,
    httpMethod: options.method,
  })

  let timesTried = 0

  function doRequest() {
    const plainFetch = async () => {
      const checkedOptions = await fetchOptionsCheck(url, options)
      return await fetch(url, checkedOptions)
    }
    timesTried++

    return plainFetch()
      .catch((err: Error) => {
        /*
         Retry on network errors
         Catch before then so that we don't retry on throws from below caused by undesirable http codes.
         */
        if (timesTried < executeOptions.tries) {
          return new Promise(function (resolve, reject) {
            setTimeout(function () {
              plainFetch().then(resolve).catch(reject)
            }, executeOptions.retryTimeout)
          })
        }

        throw err
      })
      .then(response => {
        if (timeoutID) {
          ;(response as Record<string, any>).timeoutID = timeoutID
        }
        if ((<any>response).ok) {
          return executeOptions.parseResponse(response)
        }

        return executeOptions
          .parseResponse(response)
          .then((response: PatchedResponse) => {
            // NOTE: This has been present in our fetch implementation since 2016. It was initially meant as a mechanism to return
            // information in the error type that could be used for downstream uses, like the requets logger, without knowing much about
            // every input to the fetch call that triggered the error.
            if (response.body && typeof response.body === 'object') {
              ;(response.body as StringIndexable).url = url
              ;(response.body as StringIndexable).method =
                options.method || 'GET'
            }

            throw response
          })
      })
      .then((parsedResponse: PatchedResponse) => {
        executeOptions.eventCapture.on('response:success', {
          statusCode: parsedResponse.status || constants.httpStatusCodes.OK,
          body: parsedResponse.body || {},
          headers: parsedResponse.headers,
        })

        return parsedResponse
      })
      .catch((err: PatchedResponse) => {
        executeOptions.eventCapture.on('response:failure', {
          statusCode: err.status || HTTP_FETCH_FAILURE_CODE,
          body: err.body || {},
          headers: err.headers,
        })

        throw err
      })
  }

  return doRequest()
}

// NOTE: The spec says that these bytes will be uppercased, which is why the methods are not `fetch.GET` and
// everything works as expected. The `patch` method is different because it's non-standard, yet our APIs support it
// extensively, which is why it is in here. In those cases the browser doesn't automatically uppercase and we will get
// errors when path matching in load balancers and services.
//
// Methods are defined here:
// https://httpwg.org/specs/rfc9110.html#rfc.section.9.3
//
// Whatwg-fetch relevant spec section can be found here:
// https://fetch.spec.whatwg.org/#methods
export const attachMethodHandlers = (fetchImplementation: AdcFetch) =>
  ['get', 'post', 'put', 'patch', 'delete'].forEach(method => {
    ;(fetchImplementation as StringIndexable)[method] = (
      url: string,
      opts: MethodFetchInvocationOptions
    ) =>
      fetchImplementation(url, {
        ...opts,
        method: method.toUpperCase() as HTTPMethod,
      })
  })

export const methodFactory =
  (fetchImplementation: AdcFetch, method: HTTPMethod) =>
  (url: string, opts?: MethodFetchInvocationOptions) =>
    fetchImplementation(url, { ...opts, method: method as HTTPMethod })

const createBoundedFetch = (instance: any): AdcFetch => {
  instance.__internal = instance.__internal || Object.create(null)

  const boundFetch = instance.bind(instance)

  // We attach here so modifier functions can set internal state before calling the fetch function.
  // @ts-ignore
  boundFetch.__internal = instance.__internal

  attachMethodHandlers(boundFetch as AdcFetch)

  return boundFetch
}

export const boundedFetchFactory =
  (fetchImplementation: AdcFetch) => (serviceKey: string) => {
    const boundFetch = createBoundedFetch(fetchImplementation)

    // @ts-ignore
    boundFetch.__internal.host = configment.get(serviceKey)

    return boundFetch
  }
